import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import SEO from '../components/SEO';
import { Shield, Trash2, Mail } from 'lucide-react';

// Public info page required by Google Play / Apple App Store so reviewers and
// users have an off-app route to request account deletion. Linked from the
// Play Console Data safety form.
const DeleteAccount = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
    <SEO
      title="Delete your Connectify account"
      description="How to permanently delete your Connectify eSIM account and the data we hold about you."
      canonical="/delete-account"
    />
    <Navbar />
    <section className="pt-28 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="text-sm font-bold text-gray-500 dark:text-neutral-400 hover:text-black dark:hover:text-white">← Back to home</Link>
        <div className="mt-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <Trash2 size={22} className="text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-neutral-100">Delete your account</h1>
        </div>
        <p className="mt-4 text-gray-600 dark:text-neutral-400 text-lg">
          You can permanently delete your Connectify eSIM account at any time, from inside the app or by writing to us.
        </p>

        <div className="mt-10 space-y-6">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-6">
            <h2 className="text-xl font-black text-gray-900 dark:text-neutral-100 mb-2">1 · From the mobile app</h2>
            <ol className="list-decimal pl-5 space-y-1 text-gray-700 dark:text-neutral-300">
              <li>Open the Connectify eSIM app and sign in.</li>
              <li>Go to the <span className="font-bold">Account</span> tab.</li>
              <li>Scroll to <span className="font-bold">Danger zone</span>.</li>
              <li>Tap <span className="font-bold">Delete my account</span> and type <code className="bg-gray-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded font-mono text-sm">DELETE</code> to confirm.</li>
            </ol>
          </div>

          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-6">
            <h2 className="text-xl font-black text-gray-900 dark:text-neutral-100 mb-2">2 · By email</h2>
            <p className="text-gray-700 dark:text-neutral-300">
              Can't access the app? Send an email to{' '}
              <a href="mailto:hello@connectify-esims.com?subject=Delete%20my%20account" className="font-bold text-emerald-600 dark:text-emerald-400 underline">
                hello@connectify-esims.com
              </a>{' '}
              from the address on your account with the subject <span className="font-bold">"Delete my account"</span>. We'll complete the deletion within 7 days and reply once it's done.
            </p>
            <a
              href="mailto:hello@connectify-esims.com?subject=Delete%20my%20account"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-black text-white dark:bg-white dark:text-black rounded-xl font-bold text-sm"
            >
              <Mail size={14} /> Email support
            </a>
          </div>

          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-6">
            <h2 className="text-xl font-black text-gray-900 dark:text-neutral-100 mb-2">What gets deleted</h2>
            <ul className="list-disc pl-5 space-y-1 text-gray-700 dark:text-neutral-300">
              <li>Your profile (name, email, password, phone number)</li>
              <li>Saved payment methods and auto top-up settings</li>
              <li>Push notification devices and marketing preferences</li>
              <li>Referral credits and credit history</li>
            </ul>
            <h3 className="text-base font-bold text-gray-900 dark:text-neutral-100 mt-5 mb-1">What we keep (and why)</h3>
            <p className="text-sm text-gray-600 dark:text-neutral-400">
              Order records are anonymised but retained for tax, accounting and Roamify reconciliation obligations (minimum 7 years). All personal identifiers are stripped so these records cannot be traced back to you. Already-purchased eSIMs remain active until they expire — the plan belongs to the SIM, not the account.
            </p>
          </div>

          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-neutral-400">
            <Shield size={14} />
            <span>
              More detail: see our{' '}
              <Link to="/privacy-policy" className="font-bold text-gray-900 dark:text-neutral-100 underline">Privacy policy</Link>.
            </span>
          </div>
        </div>
      </div>
    </section>
    <Footer />
  </div>
);

export default DeleteAccount;
